import * as React from "react";
import {
  Checkbox,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  Stack,
  ChoiceGroup,
  DatePicker,
  DayOfWeek,
  IChoiceGroupOption,
  IconButton,
  Label,
} from "@fluentui/react"; // still v8 for these controls
import { ArrowSwap24Filled } from "@fluentui/react-icons";
import { useParams, useNavigate } from "react-router-dom";
import { Input, Spinner, useToastController } from "@fluentui/react-components";

import {
  AdminService,
  CouponDto as CouponApiDto,
  Modul,
} from "../../KulcsUzletSvc";
import { msalInstance } from "../../utils/auth/AuthProvider";
import { ToasterContext } from "../../utils/GlobalContexts";
import { notify } from "../../utils/DispatchToaster";
import { TOAST } from "../../utils/Enums";
import { CouponType } from "./MerchantCouponsPage";
import {
  formatTaxNumberInput,
  isFormattedTaxNumberValid,
} from "../../utils/taxNumber";

const BASE_PRICE = 3810;

const couponTypeLabels: Record<CouponType, string> = {
  [CouponType.All]: "Mindenre",
  [CouponType.OnlyBase]: "Csak alapmodulra",
  [CouponType.OnlyModul]: "Csak kiegészítő modulokra",
  [CouponType.SpecifyModul]: "Csak modulra",
  [CouponType.OnlyBaseOnlyOneUser]:
    "Egyszer használható kupon, csak alapmodulra",
};

// Transform into FluentUI options
const couponTypes: IDropdownOption[] = Object.values(CouponType).map(
  (type) => ({
    key: type,
    text: couponTypeLabels[type],
  }),
);

// modul lista a backendből (Admin/moduls), a modulId mezőhöz valós GUID kell
const mapModulToOption = (m: Modul): IDropdownOption => ({
  key: m.id ?? "",
  text: `${m.name} - ${
    m.monthPrice === 0 ? "ingyenes" : m.monthPrice + " Ft/hó"
  }`,
});

const generateCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 11; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const NewCouponForm: React.FC = () => {
  const { toasterId } = React.useContext(ToasterContext);
  const { dispatchToast } = useToastController(toasterId);

  type CouponFormState = {
    merchant: string;
    code: string;
    type: CouponType;
    module: string;
    discount: number;
    price: number;
    activationDeadline: string;
    validityDays: number;
    validityDate: string;
  };

  const [formData, setFormData] = React.useState<CouponFormState>({
    merchant: "",
    code: "",
    type: CouponType.All,
    module: "",
    discount: 10,
    price: BASE_PRICE * 0.9,
    activationDeadline: "",
    validityDays: 365,
    validityDate: "2026-08-22",
  });

  const [isFixedAmount, setIsFixedAmount] = React.useState(false);
  const [discountAmount, setDiscountAmount] = React.useState<number | string>(
    "",
  );
  const [taxNumber, setTaxNumber] = React.useState("");
  const [redeemableUntil, setRedeemableUntil] = React.useState<Date | null>(
    null,
  );
  const [validUntil, setValidUntil] = React.useState<Date | null>(null);
  const [validDays, setValidDays] = React.useState<number | string>("");
  const [moduleOptions, setModuleOptions] = React.useState<IDropdownOption[]>(
    [],
  );
  const [isSaving, setIsSaving] = React.useState(false);

  const { id } = useParams();
  React.useEffect(() => {
    if (!id) return;
    const fetchMerchant = async () => {
      try {
        const res = await AdminService.getAdminApplicationUser(id);
        if (res) {
          setFormData((p) => ({ ...p, merchant: res.fullName ?? "" }));
        }
      } catch (e) {
        console.log("Nem található a kereskedő:", e);
      }
    };
    fetchMerchant();
  }, [id]);

  React.useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await AdminService.getAdminModuls();
        // Webshop modul nem választható kuponhoz
        setModuleOptions(
          (res ?? [])
            .filter((m) => m.name !== "Kulcs Flow Webshop")
            .map(mapModulToOption),
        );
      } catch (e) {
        console.log("Nem sikerült betölteni a modulokat:", e);
      }
    };
    fetchModules();
  }, []);
  const [validityMode, setValidityMode] = React.useState<"days" | "date">(
    "days",
  );
  const expiryOptions: IChoiceGroupOption[] = [
    { key: "days", text: "Beváltás után x napig érvényes" },
    { key: "date", text: "Konkrét dátumig érvényes" },
  ];

  const handleChange = <K extends keyof CouponFormState>(
    field: K,
    value: CouponFormState[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscountChange = (value: string) => {
    let discount = parseFloat(value) || 0;

    // Clamp between 1 and 99
    if (discount < 1) discount = 1;
    if (discount > 99) discount = 99;
    const newPrice = Math.round(BASE_PRICE * (1 - discount / 100));
    setFormData((prev) => ({ ...prev, discount, price: newPrice }));
  };

  const handlePriceChange = (value: string) => {
    const price = parseFloat(value) || 0;
    const discount = Math.round(((BASE_PRICE - price) / BASE_PRICE) * 100);
    setFormData((prev) => ({ ...prev, price, discount }));
  };

  const handleSubmit = () => {
    (async () => {
      setIsSaving(true);
      try {
        let validatedTaxNumber: string | undefined;

        // Basic validation
        if (!formData.code) {
          alert("Kérem adjon meg egy kuponkódot!");
          return;
        }

        if (isFixedAmount) {
          const amount = Number(discountAmount);
          if (!amount || amount <= 0) {
            alert("Kérem adjon meg egy kedvezmény összeget!");
            return;
          }
          if (!redeemableUntil) {
            alert("Kérem adja meg, meddig érvényesíthető a kupon!");
            return;
          }

          const rawTax = String(taxNumber || "").trim();
          if (rawTax) {
            const formattedTax = formatTaxNumberInput(rawTax);

            if (!isFormattedTaxNumberValid(formattedTax)) {
              alert("Az adószám formátuma hibás. Várt: 12345678-1-12");
              return;
            }

            validatedTaxNumber = formattedTax;
          }
        }
        if (!id) {
          alert("Nincs kereskedő azonosító.");
          return;
        }

        const mapCouponType = (t: string) => {
          switch (t) {
            case CouponType.OnlyBase:
              return 1;
            case CouponType.OnlyModul:
              return 2;
            case CouponType.SpecifyModul:
              return 3;
            case CouponType.OnlyBaseOnlyOneUser:
              return 4;
            case CouponType.All:
            default:
              return 0;
          }
        };

        const isGuid = (v: string) =>
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
            v,
          );

        // helper: try to resolve the current admin ApplicationUser id by MSAL account
        const resolveApplicationUserId = async (): Promise<string | null> => {
          try {
            const account =
              msalInstance.getActiveAccount() ||
              (msalInstance.getAllAccounts && msalInstance.getAllAccounts()[0]);
            if (!account) return null;
            // Try several candidate identifiers from the MSAL account / id token claims
            const claims = (account as any).idTokenClaims || {};
            const candidates: Array<string | undefined> = [
              (account as any).username,
              (account as any).name,
              claims.preferred_username,
              claims.upn,
              claims.email,
              (account as any).homeAccountId,
            ];

            for (const c of candidates) {
              if (!c) continue;
              try {
                const res = await AdminService.getAdminApplicationUsers(
                  c,
                  undefined,
                  undefined,
                  1,
                );
                const found =
                  res?.items && res.items.length > 0 ? res.items[0] : null;
                if (found && found.id) return found.id;
              } catch (e) {
                // ignore and try next candidate
              }
            }

            return null;
          } catch (e) {
            console.log("Failed to resolve ApplicationUser id:", e);
            return null;
          }
        };

        const appUserId = await resolveApplicationUserId();

        // Prefer ApplicationUser id for creator/modifier, but fall back to traderId
        // if no mapping exists (to avoid sending null which causes backend 500).
        const resolvedCreatorId = appUserId ?? id;

        const payload: Partial<CouponApiDto> & {
          discountValue?: number;
          taxNumber?: string;
        } = {
          couponCode: formData.code,
          couponType: mapCouponType(formData.type as unknown as string),
          // modulId must be a GUID or null for the backend
          modulId: isGuid(String(formData.module)) ? formData.module : null,
          discount: isFixedAmount ? undefined : Number(formData.discount),
          traderId: id,
          // creator/modifier set below (either ApplicationUser.id or traderId fallback)
        };

        if (isFixedAmount) {
          const amount = Number(discountAmount);
          payload.discountValue = amount;
        }

        if (isFixedAmount && validatedTaxNumber) {
          payload.taxNumber = validatedTaxNumber;
        }

        if (isFixedAmount) {
          payload.validDays = 0;
          payload.validTo = redeemableUntil?.toISOString();
          payload.expiredDate = redeemableUntil?.toISOString();
        }

        if (resolvedCreatorId) {
          payload.creatorId = resolvedCreatorId;
          payload.modifierId = resolvedCreatorId;
        }

        if (!isFixedAmount) {
          if (validityMode === "date") {
            if (validUntil) {
              payload.validTo = validUntil.toISOString();
              // keep expiredDate aligned with validTo for clarity
              payload.expiredDate = validUntil.toISOString();
            }
          } else {
            const vd = Number(validDays) || Number(formData.validityDays);
            payload.validDays = vd;
            // compute a concrete validTo from today + vd days so backend receives explicit date
            try {
              const now = new Date();
              const expiry = new Date(now.getTime() + vd * 24 * 60 * 60 * 1000);
              payload.validTo = expiry.toISOString();
              payload.expiredDate = expiry.toISOString();
            } catch (e) {
              console.warn("Failed to compute validTo from validDays", e);
            }
          }
        }

        // A kuponkód egyediségét a backend ellenőrzi, a válasz isSuccess/errorMessage mezője jelzi az eredményt
        const res = await AdminService.postAdminCoupon(payload as any);

        if (res?.isSuccess) {
          navigate(`/admin/trader/${id}/coupons`);
        } else {
          // üzleti validáció (pl. duplikált kód) - figyelmeztetés, nem hiba
          notify(
            TOAST.WARN,
            res?.errorMessage || "A kupon mentése nem sikerült.",
            dispatchToast,
          );
        }
      } catch (e) {
        console.error(e);
        notify(
          TOAST.ERROR,
          "Hiba történt a kupon mentése közben.",
          dispatchToast,
        );
      } finally {
        setIsSaving(false);
      }
    })();
  };

  const navigate = useNavigate();

  const isModuleDisabled = formData.type !== CouponType.SpecifyModul;

  return (
    <Stack
      tokens={{ childrenGap: 15 }}
      styles={{ root: { maxWidth: 600, margin: "0 auto" } }}
    >
      <h2 style={{ textAlign: "center" }}>{`Új kupon - ${
        formData.merchant || "Kereskedő"
      }
      `}</h2>

      <Label>Kereskedő neve</Label>
      <Input value={formData.merchant} readOnly />

      <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="end">
        <Stack.Item grow>
          <Label>Kuponkód</Label>
          <Input
            value={formData.code}
            onChange={(_, data) => handleChange("code", data.value)}
          />
        </Stack.Item>

        <IconButton
          title="Kód generálása"
          ariaLabel="Kód generálása"
          styles={{
            root: {
              backgroundColor: "#7dd3fc",
              color: "white",
              borderRadius: 4,
              height: 32,
              width: 32,
            },
            rootHovered: {
              backgroundColor: "#38bdf8",
            },
          }}
          onRenderIcon={() => <ArrowSwap24Filled style={{ fontSize: 20 }} />}
          onClick={() => setFormData({ ...formData, code: generateCode() })}
        />
      </Stack>

      <Dropdown
        label="Kupon típusa"
        selectedKey={formData.type}
        options={couponTypes}
        onChange={(_, option) => {
          if (!option) return;
          handleChange("type", option.key as CouponType);
        }}
      />

      <Dropdown
        label="Modul"
        selectedKey={formData.module}
        options={moduleOptions}
        disabled={isModuleDisabled}
        onChange={(_, option) => {
          if (!option) return;
          handleChange("module", String(option.key));
        }}
      />

      <Checkbox
        label="Fix összegű"
        checked={isFixedAmount}
        onChange={(_, checked) => setIsFixedAmount(!!checked)}
      />

      {isFixedAmount ? (
        <>
          <Label>Kedvezmény összege (Ft)</Label>
          <Input
            type="number"
            value={discountAmount.toString()}
            onChange={(_, data) => setDiscountAmount(data.value)}
          />

          <DatePicker
            label="Meddig érvényesíthető"
            firstDayOfWeek={DayOfWeek.Monday}
            value={redeemableUntil || undefined}
            onSelectDate={(date) => setRedeemableUntil(date || null)}
            placeholder="Válassz dátumot"
          />

          <Label>Adószám (opcionális)</Label>
          <Input
            type="text"
            value={taxNumber}
            onChange={(_, data) =>
              setTaxNumber(formatTaxNumberInput(data.value))
            }
          />
        </>
      ) : (
        <>
          <Label>Kedvezmény (%)</Label>
          <Input
            min={1}
            max={99}
            type="number"
            value={formData.discount.toString()}
            onChange={(_, data) => handleDiscountChange(data.value)}
          />

          <Label>{`Alap modul ára ${BASE_PRICE} Ft helyett havonta (Ft)`}</Label>
          <Input
            type="number"
            value={formData.price.toString()}
            onChange={(_, data) => handlePriceChange(data.value)}
          />

          <Label>
            Meddig aktiválható (Eddig lehet beírni a kupont előfizetéskor)
          </Label>
          <Input
            value={formData.activationDeadline}
            onChange={(_, data) =>
              handleChange("activationDeadline", data.value)
            }
          />

          <ChoiceGroup
            selectedKey={validityMode}
            options={expiryOptions}
            onChange={(_, option) => {
              const mode = option?.key as "days" | "date";
              setValidityMode(mode);
              // clear the other input when switching modes to avoid stale values
              if (mode === "date") {
                setValidDays("");
              } else {
                setValidUntil(null);
              }
            }}
          />

          {validityMode === "days" && (
            <>
              <Label>Meddig érvényes (nap)</Label>
              <Input
                type="number"
                value={validDays.toString()}
                onChange={(_, data) => setValidDays(data.value)}
              />
            </>
          )}

          {validityMode === "date" && (
            <DatePicker
              label="Meddig érvényes (dátum)"
              firstDayOfWeek={DayOfWeek.Monday}
              value={validUntil || undefined}
              onSelectDate={(date) => setValidUntil(date || null)}
              placeholder="Válassz dátumot"
            />
          )}
        </>
      )}

      <Stack horizontal tokens={{ childrenGap: 10 }}>
        <DefaultButton
          text="Mégse"
          onClick={() => {
            if (id) navigate(`/admin/trader/${id}/coupons`);
            else navigate(-1);
          }}
        />
        <PrimaryButton
          text={isSaving ? "Mentés..." : "Mentés"}
          onClick={handleSubmit}
          disabled={isSaving}
          onRenderIcon={isSaving ? () => <Spinner size="tiny" /> : undefined}
        />
      </Stack>
    </Stack>
  );
};
